/**
 * Menu superior do redesign (JOR4-7365).
 *
 * Substitui o dxToolbar de #toolbar. Ordem e conteúdo seguem o protótipo:
 * hambúrguer, logo, espaçador, Novidades, seletor de empresa/filial, sino e
 * avatar.
 *
 * O botão "Design System" do protótipo é da documentação e não é implementado.
 */

import { Menu, Sparkles } from "lucide-react";
import { createRoot, Root } from "react-dom/client";

import { MenuUsuario } from "./MenuUsuario";
import { SeletorEmpresa, type Filial } from "./SeletorEmpresa";

export interface EstadoTopbar {
    empresa: string;
    filiais: Filial[];
    nomeUsuario: string;
    siglaUsuario: string;
    /** O atalho de novidades só existe no White Label da Logae. */
    mostrarNovidades: boolean;
    aoAlternarMenu: () => void;
    aoAbrirNovidades: () => void;
    aoTrocarEmpresa: () => void;
    aoSair: () => void;
    /**
     * Recebe o elemento onde o sino é montado. O HermesNotifier é um widget
     * DevExtreme e monta a si próprio num elemento; o React só reserva o lugar
     * e nunca desenha filhos ali, para não competir pelo DOM.
     */
    aoMontarNotificacoes: (elemento: HTMLDivElement | null) => void;
}

export const Topbar = ({
    empresa,
    filiais,
    nomeUsuario,
    siglaUsuario,
    mostrarNovidades,
    aoAlternarMenu,
    aoAbrirNovidades,
    aoTrocarEmpresa,
    aoSair,
    aoMontarNotificacoes,
}: EstadoTopbar) => (
    <header className="topbar-ds">
        <button
            type="button"
            className="topbar-ds-icone"
            onClick={aoAlternarMenu}
            aria-label="Alternar menu"
            title="Alternar menu"
        >
            <Menu size={18} />
        </button>

        {/* O logo vem do tema (--dx-theme-logo-toolbar), então é fundo de um
            elemento e não <img>: é assim que o White Label o troca. */}
        <div className="topbar-ds-logo" />

        <div className="topbar-ds-espacador" />

        {mostrarNovidades && (
            <button
                type="button"
                className="topbar-ds-texto"
                onClick={aoAbrirNovidades}
            >
                <Sparkles size={12} />
                <span>Novidades</span>
            </button>
        )}

        <SeletorEmpresa
            empresa={empresa}
            filiais={filiais}
            aoTrocarEmpresa={aoTrocarEmpresa}
        />

        <div className="topbar-ds-notificacoes" ref={aoMontarNotificacoes} />

        <MenuUsuario
            nomeUsuario={nomeUsuario}
            siglaUsuario={siglaUsuario}
            aoSair={aoSair}
        />
    </header>
);

/**
 * Monta o menu superior num elemento e devolve controle imperativo, pelo mesmo
 * motivo do menu lateral: quem hospeda é uma classe jQuery/DevExtreme.
 */
export const criarTopbar = (elemento: HTMLElement, estado: EstadoTopbar) => {
    const root: Root = createRoot(elemento);
    let atual = estado;

    const desenhar = () => root.render(<Topbar {...atual} />);

    desenhar();

    return {
        atualizar(parcial: Partial<EstadoTopbar>) {
            atual = { ...atual, ...parcial };
            desenhar();
        },
        destruir() {
            root.unmount();
        },
    };
};
